import { KeyboardEvent, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { useLayout } from "@/contexts/LayoutContext";
import { useClickOutside } from "@/hooks/useClickOutside";
import { showErrorToast } from "@/hooks/use-toast";
import { SearchType } from "@/types/domain";
import { openSearchLookup } from "@/utils/searchLookup";
import { searchAutocomplete, getRedirectUrl } from "@/api/autocomplete/api";
import { SearchResultItem } from "@/types/domain";

type SearchBarProps = {
  variant?: "inline" | "stacked";
}

export const SearchBar = ({ variant = "inline" }: SearchBarProps) => {
  const { data } = useLayout();
  const routes = data.appRoutes;
  const stacked = variant === "stacked";
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [suggestions, setSuggestions] = useState<SearchResultItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recentCacheRef = useRef<SearchResultItem[] | null>(null);

  const getSearchType = () => { return selectRef.current?.value ?? ""; }
  const getSearchText = () => { return inputRef.current?.value ?? ""; }
  const getSearchDesc = () => {
    const opt = selectRef.current?.options[selectRef.current.selectedIndex];
    return opt?.text ?? "";
  }

  const isCompanyCategory = () => {
    // Company category items have parentOptionGroup === "Company" or id === "Company"
    const selected = data.searchTypes.find((s) => s.id === getSearchType());
    return selected?.parentOptionGroup === "Company" || selected?.id === "Company";
  }

  const fetchSuggestions = (query: string) => {
    const category = getSearchType();
    const categoryName = getSearchDesc();
    const isCompany = isCompanyCategory();

    // For Company with empty query, use cache if available
    if (query === "" && isCompany && recentCacheRef.current) {
      const withSep = recentCacheRef.current.length > 0
        ? [{ id: "__seperator__", label: "Recent", value: "", category: "", target: "" }, ...recentCacheRef.current]
        : [];
      setSuggestions(withSep);
      setShowSuggestions(withSep.length > 0);
      setActiveIndex(-1);
      return;
    }

    searchAutocomplete(query, category, categoryName)
      .then((items) => {
        // Cache empty-query Company results as "recents"
        if (query === "" && isCompany) recentCacheRef.current = items;
        // Prepend separator when showing only recents (empty query)
        if (query === "" && isCompany && items.length > 0) {
          const withSep = [{ id: "__seperator__", label: "Recent", value: "", category: "", target: "" }, ...items];
          setSuggestions(withSep);
          setShowSuggestions(true);
          setActiveIndex(-1);
          return;
        }
        // For non-empty Company queries, append cached recents at the bottom
        let merged = items;
        if (query !== "" && isCompany && recentCacheRef.current) {
          const recentIds = new Set(items.map((i) => i.id));
          const extras = recentCacheRef.current.filter((r) => !recentIds.has(r.id));
          if (extras.length > 0) merged = [...items, { id: "__seperator__", label: "--- Recent ---", value: "", category: "", target: "" }, ...extras];
        }
        setSuggestions(merged);
        setShowSuggestions(merged.length > 0);
        setActiveIndex(-1);
      })
      .catch(() => setSuggestions([]));
  }

  const handleInputFocus = () => {
    // On focus, if Company category and input is empty, show recents immediately
    if (isCompanyCategory() && !getSearchText()) {
      fetchSuggestions("");
    }
  }

  const handleInputChange = () => {
    const val = getSearchText();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length === 0) {
      if (isCompanyCategory()) {
        fetchSuggestions("");
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  }

  const selectSuggestion = (item: SearchResultItem) => {
    if (inputRef.current) inputRef.current.value = item.value;
    setShowSuggestions(false);
    setSuggestions([]);
    if (item.target) {
      getRedirectUrl(item.id, item.target, "")
        .then((redirect) => { if (redirect) window.location.assign(routes.root + redirect); })
        .catch(() => { showErrorToast("Failed to navigate to result."); });
    }
  }

  const handleSearch = () => {
    setShowSuggestions(false);
    openSearchLookup(routes, getSearchType(), getSearchText(), getSearchDesc());
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (showSuggestions && activeIndex >= 0) {
        selectSuggestion(suggestions[activeIndex]);
      } else {
        handleSearch();
      }
      return;
    }
    if (!showSuggestions) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => {
        let next = Math.min(i + 1, suggestions.length - 1);
        while (next < suggestions.length - 1 && suggestions[next]?.id === "__seperator__") next++;
        return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => {
        let prev = Math.max(i - 1, -1);
        while (prev > 0 && suggestions[prev]?.id === "__seperator__") prev--;
        return prev;
      });
    }
    else if (e.key === "Escape") { setShowSuggestions(false); }
  }

  // Close dropdown on outside click
  useClickOutside(containerRef, () => setShowSuggestions(false));

  // Build grouped search options
  const optionGroups: Map<string, SearchType[]> = new Map();
  const topLevelOptions: SearchType[] = [];
  for (const item of data.searchTypes) {
    if (item.isOptionGroupItem) {
      // group header — skip, rendered via optgroup
    } else if (item.parentOptionGroup) {
      const group = optionGroups.get(item.parentOptionGroup) ?? [];
      group.push(item);
      optionGroups.set(item.parentOptionGroup, group);
    } else {
      topLevelOptions.push(item);
    }
  }
  const groupHeaders = data.searchTypes.filter((s) => s.isOptionGroupItem);

  return (
    <div ref={containerRef} className={stacked ? "relative w-full p-2" : "flex items-center h-14.5 relative"}>
      <div className={stacked ? "flex flex-col rounded-lg overflow-hidden border border-black/20 w-full" : "flex items-stretch h-9 rounded-2xl overflow-hidden border border-black/20"}>
        {/* Type selector */}
        <select
          ref={selectRef}
          defaultValue={data.selectedSearchType}
          className={stacked ? "bg-white text-black text-sm px-2 py-1.5 border-b border-black/10 focus:outline-none w-full" : "bg-white text-black text-sm px-1 border-r border-black/10 focus:outline-none max-w-40"}
          aria-label="Search type"
        >
          {topLevelOptions.map((item) => (
            <option key={item.id} value={item.id}>{item.description}</option>
          ))}
          {groupHeaders.map((header) => {
            const children = optionGroups.get(header.id) ?? [];
            return (
              <optgroup key={header.id} label={header.description}>
                {children.map((child) => (
                  <option key={child.id} value={child.id}>{child.description}</option>
                ))}
              </optgroup>
            );
          })}
        </select>

        {/* Text input + submit row */}
        <div className="flex items-stretch">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search..."
            autoComplete="off"
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            className={stacked ? "bg-white text-black placeholder-black/40 text-sm px-2 py-1.5 flex-1 focus:outline-none" : "bg-white text-black placeholder-black/40 text-sm px-2 w-28 lg:w-36 xl:w-50 focus:outline-none"}
            aria-label="Search text"
            aria-autocomplete="list"
            aria-expanded={showSuggestions}
          />

          {/* Submit button */}
          <button
            type="button"
            onClick={handleSearch}
            className="flex items-center justify-center px-2 bg-white text-black transition-colors focus:outline-none border-l border-black/10"
            aria-label="Execute search"
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" />
          </button>
        </div>
      </div>

      {/* Autocomplete suggestions */}
      {showSuggestions && (
        <ul className="absolute top-full left-0 mt-1 w-full min-w-80 bg-white border border-border rounded shadow-lg z-30 max-h-72 overflow-y-auto text-sm text-black">
          {suggestions.map((item, i) => {
            if (item.id === "__seperator__") {
              return (
                <li key="__seperator__" className="px-3 py-1 text-xs text-black/40 font-semibold select-none border-t border-black/10 mt-1">
                  Recent
                </li>
              );
            }
            return (
              <li
                key={`${item.id}-${i}`}
                className={`px-3 py-1.5 cursor-pointer ${i === activeIndex ? "bg-accent" : "hover:bg-accent"}`}
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(item); }}
              >
                {item.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

